import { Fragment } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setPageValue } from "../../redux/slices/datacorrectionpageslice";
import {
  Toolbar,
  ToolbarItem,
  ToolbarSeparator,
  Button,
  DropDownButton,
} from "@progress/kendo-react-buttons";
import { Input, Checkbox } from "@progress/kendo-react-inputs";
import { Form } from "../../components/form";
import { Dialog } from "@progress/kendo-react-dialogs";
import { List } from "../../components/list";
import {
  getDataTypeIcon,
  getExpressionIcon,
  sortDirections,
} from "../../lib/utils";
import { ComputedFieldFunction } from "../../lib/computedfieldfunction";
import {
  DropDownListById,
  FormDropDownList,
  FormDropDownListById,
} from "../../forms/form-components";
import { disAllowedCharactersInTitle } from "../../settings";
import { Field } from "@progress/kendo-react-form";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { fi } from "date-fns/locale";
import i18next from "i18next";
import { useTranslation } from "react-i18next";

const listItemRenderer =
  (pageState, currentTheme, formRenderProps, t) => (props, item, index) => {
    const {
      formRenderProps,
      onDragStart,
      onDragOver,
      onDrop,
      onDragStartHelper,
      onDragOverHelper,
      onDropHelper,
      onFieldToUseChange,
      onNewValueChange,
      onFieldTitleChange,
      ...other
    } = props;

    const dataTypeIcon = getDataTypeIcon(item.DataTypeId);
    const expressionIcon = getExpressionIcon(item.FieldIsAggregate);

    const onFieldRemove = props.onFieldRemove;
    const onToggleEquation = props.onToggleEquation;

    const handleOnDragStart = (e) => {
      let data = onDragStartHelper(e, item);
      if (onDragStart) onDragStart(e, data, formRenderProps);
    };

    const handleOnDragOver = (e) => {
      onDragOverHelper(e);

      if (onDragOver) onDragOver(e, formRenderProps);
    };

    const handleOnDrop = (e) => {
      let data = onDropHelper(e);
      if (onDrop) onDrop(e, data, formRenderProps);
    };

    //let tooltipText = item.IsComputedField ? "You can drag & drop to reorder, and double click to edit" : "You can drag & drop to reorder";
    let tooltipText = "";

    //console.log(item)
    if (item.HasComputedFunction) {
      let computedFunction = ComputedFieldFunction.getById(item.FunctionId);
      let parentField = pageState.formValues.Fields.find(
        (e) => e.Id == item.ParentFieldId
      );
      if (parentField) {
        let title = computedFunction.title + "(" + parentField.Title + ")";
        tooltipText = title; // + "\r\n" + tooltipText;
      }
    } else if (item.HasExpressionBody)
      tooltipText = t("datacorrection.expressionfield") + item.ExpressionBody;
    else {
      let obj = pageState.currentDataset.Fields.find(
        (e) => e.Id == item.FieldId
      );
      if (obj) {
        if (obj.IsExpression) {
          if (obj.IsAggregate)
            tooltipText = `${t("datacorrection.aggregateExpression")}: ${
              obj.ExpressionBody
            }`;
          else
            tooltipText = `${t("datacorrection.expression")}: ${
              obj.ExpressionBody
            }`;
        } else tooltipText = `${t("datacorrection.field4")}: ${obj.Title}`;
      }
    }

    const txtLength = pageState.currentDataset.Fields.find(
      (e) => e.Id == item.FieldId
    )?.Lenght;

    return (
      <div
        key={item.Id}
        className={`list-item`}
        title={tooltipText}
        data-drag-id={item.Id}
        draggable="true"
        onDragStart={handleOnDragStart}
        onDragOver={handleOnDragOver}
        onDrop={handleOnDrop}
      >
        <div style={{ fontWeight: 700 }}>{index + 1} -</div>
        <div
          className={`list-item-data-type-icon k-icon ${dataTypeIcon} `}
        ></div>
        {/* <div
          className={`list-item-expression-icon k-icon ${expressionIcon}`}
        ></div> */}
        <div>
          <Input maxLength={250} disabled value={item.Title} width={200} />
          {item.IsEquation && (
            <>
              <DropDownList
                id="FieldToUse"
                name="FieldToUse"
                placeholder={t("datacorrection.fielduse")}
                component={FormDropDownListById}
                data={pageState.currentDataset.Fields.filter(
                  (field) => field.DataTypeId === 2
                )}
                dataItemKey="Id"
                textField="Title"
                onChange={(e) => onFieldToUseChange(item.Id, e.value)}
                disabled={pageState.processing || pageState.formValues.IsSystem}
                style={{ width: "300px", margin: "15px" }}
              />
              <DropDownList
                placeholder={t("datacorrection.operation")}
                component={FormDropDownList}
                onChange={(e) =>
                  props.onOperatorChange(item.Id, e.target.value)
                }
                data={["+", "-", "/", "*"]}
                style={{
                  width: "80px",
                }}
              />
            </>
          )}
          <Input
            style={{ marginLeft: "15px" }}
            placeholder={t("datacorrection.newvalue")}
            id="NewValue"
            name="NewValue"
            {...(txtLength > -1 ? { maxLength: txtLength } : {})}
            onChange={(e) => onNewValueChange(item.Id, e.target.value)}
          />
        </div>

        <div className="list-item-delete">
          {item.DataTypeId == 2 && (
            <Button
              disabled={pageState.processing || pageState.formValues.IsSystem}
              style={{
                border: "none",
                color: currentTheme.touchColor,
                backgroundColor: "transparent",
              }}
              icon="sum"
              onClick={(e) => onToggleEquation(item.Id)}
            ></Button>
          )}
        </div>
        <div className="list-item-delete">
          <Button
            disabled={pageState.processing || pageState.formValues.IsSystem}
            style={{
              border: "none",
              color: currentTheme.error,
              backgroundColor: "transparent",
            }}
            icon="close"
            onClick={(e) => onFieldRemove(item, formRenderProps)}
          ></Button>
        </div>
      </div>
    );
  };

const dialogListItemRenderer = (currentTheme) => (props, item, index) => {
  const dataTypeIcon = getDataTypeIcon(item.DataTypeId);
  const expressionIcon = getExpressionIcon(item.FieldIsAggregate);
  const onFieldCheck = props.onFieldCheck;

  return (
    <div key={item.FieldId} className={`list-item`}>
      <div style={{ width: 28 }}>
        <Checkbox value={item.Checked} onChange={(e) => onFieldCheck(item)} />
      </div>
      <div className={`list-item-data-type-icon k-icon ${dataTypeIcon} `}></div>
      <div
        className={`list-item-expression-icon k-icon ${expressionIcon}`}
      ></div>
      <div className="list-item-title">{item.Title}</div>
    </div>
  );
};

const toolbarRenderer = (props) => {
  const {
    dispatch,
    pageState,
    fields,
    currentTheme,
    onSave,
    onClose,
    t,
    ...other
  } = props;

  return (
    <Fragment>
      <div className="datacorrectionpage-fields-dialog toolbar">
        <Toolbar>
          <ToolbarItem>
            <Button
              style={{ color: currentTheme.primary }}
              icon="save"
              onClick={onSave}
            >
              {t("datacorrection.save")}
            </Button>
          </ToolbarItem>
          <ToolbarSeparator />
          <ToolbarItem>
            <Input
              tabIndex={0}
              placeholder={t("datacorrection.locate")}
              style={{ width: 320 }}
              onChange={(e) => {
                let title = e.value.trim().toLowerCase();
                let fieldGroups = pageState.currentDataset.FieldGroups;

                dispatch(
                  setPageValue({
                    id: "fieldsToAdd",
                    value: fields.map((item) => {
                      if (item.Title.toLowerCase().indexOf(title) >= 0)
                        return { ...item, IsVisible: true };
                      else return { ...item, IsVisible: false };
                    }),
                  })
                );
              }}
            />
          </ToolbarItem>
          <ToolbarSeparator />
          <ToolbarItem>
            <Button
              style={{ color: currentTheme.primary }}
              icon="close"
              onClick={onClose}
            >
              {t("datacorrection.close")}
            </Button>
          </ToolbarItem>
        </Toolbar>
      </div>
      <style>
        {`
                    /*form toolbar*/
                    .datacorrectionpage-fields-dialog.toolbar .k-toolbar
                    {
                      /*width: 100%;*/
                      border-left: none !important;
                      border-bottom: 1px solid ${currentTheme.separatorColor};
                      border-top: none !important;
                      border-right: none !important;
                      /*padding: 5px 4px 5px 0px !important;
                      background-color: ${currentTheme.dialogToolbarBg}!important;*/
                    }

                    .datacorrectionpage-fields-dialog.toolbar .k-button
                    {
                      /*the toolbar buttons*/
                      border: none;
                      padding: 6px 8px;
                    }

                    .datacorrectionpage-fields-dialog.toolbar .k-separator 
                    {
                        border-color: ${currentTheme.appBarLinkColor} !important;
                    }

                    .datacorrectionpage-fields-dialog.toolbar .k-toolbar .k-split-button:focus, .form-root .toolbar .k-toolbar .k-split-button.k-focus,
                    .datacorrectionpage-fields-dialog.toolbar .k-toolbar .k-button-solid-base:focus, .form-root .toolbar .k-toolbar .k-button-solid-base.k-focus
                    {
                        box-shadow: none !important;
                        border: none !important;
                    }

                    .datacorrectionpage-fields-dialog.toolbar .k-toolbar .k-button:active::before, .datacorrectionpage-fields-dialog.toolbar .k-toolbar .k-button:hover::before,
                    .datacorrectionpage-fields-dialog.toolbar .k-toolbar .k-button:focus::before, .datacorrectionpage-fields-dialog.toolbar .k-toolbar .k-button:focus-within::before                        
                    {
                        opacity: 0 !important;
                        border: none !important;
                        box-shadow: none !important;
                    }

                    .datacorrectionpage-fields-dialog.toolbar .k-toolbar .k-dropdown-button.k-focus .k-button::after
                    {
                        /*border: 1px solid ${currentTheme.componentBg} !important;*/
                        box-shadow: none !important;
                    }     

                    .datacorrectionpage-fields-dialog input
                    {
                        background-color: transparent;
                        color: ${currentTheme.dialogToolbarText};
                        border: none;
                    }

                    .datacorrectionpage-fields-dialog input:focus
                    {
                        background-color: transparent;
                        color: ${currentTheme.dialogToolbarText};
                        border: none;
                    }

                    .datacorrectionpage-fields-dialog input::placeholder
                    {
                        color: ${currentTheme.dialogToolbarText};
                    }
                    /*form toolbar*/
                    `}
      </style>
    </Fragment>
  );
};

const actionRenderer = (currentTheme) => (props) => {
  if (props.item.actionName == "")
    return (
      <div
        className="separator"
        disabled={true}
        style={{
          width: "100%",
          height: 1,
          borderBottom: `1px solid ${currentTheme.componentBorderColor}`,
        }}
      ></div>
    );

  return (
    <div>
      <span className={`k-icon k-i-${props.item.icon}`} role="presentation" />
      <span style={{ paddingLeft: 16 }}>{props.item.text}</span>
    </div>
  );
};

export const TableValueList = (props) => {
  const currentTheme = useSelector((state) => state.currentTheme);
  const pageState = useSelector((state) => state.dataCorrectionPage);
  const formRenderProps = props.formRenderProps;
  const { t } = useTranslation(); 

  const actions = [
    {
      actionName: "1",
      text:`${t("datacorrection.addfields")}`,
      icon: "plus",
    },
    {
      actionName: "",
      disabled: true,
    },
    {
      actionName: "5",
      text: `${t("datacorrection.clear")}`,
      icon: "close",
      //disabled: true,
    },
  ];

  return (
    <Fragment>
      {
        <div className="table-section">
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "0px 8px 2px 8px",
              borderBottom: `1px solid ${currentTheme.componentBorderColor}`,
            }}
          >
            <div style={{ fontSize: 14 }}>{t("datacorrection.fieldstoupdate")}</div>
            <DropDownButton
              popupSettings={{ popupClass: "table-datacorrection-actions" }}
              disabled={pageState.processing || pageState.formValues.IsSystem}
              buttonClass="table-datacorrection-actions-button"
              style={{ fontSize: 18, fontWeight: 700 }}
              fillMode={"flat"}
              itemRender={actionRenderer(currentTheme)}
              items={actions}
              textField="text"
              icon="more-horizontal"
              onItemClick={(e) => {
                let id = e.item.actionName;

                if (id == "1") props.onOpenFieldsDialog();
                else if (id == "5") props.onFieldsClear(e, formRenderProps);
              }}
            />
          </div>
          {pageState.formValues.Fields && (
            <List
              className="table-fields"
              formRenderProps={formRenderProps}
              items={pageState.formValues.Fields}
              itemRenderer={listItemRenderer(
                pageState,
                currentTheme,
                props.formRenderProps,
                t
              )}
              onDragStart={props.onFieldDragStart}
              onDragOver={props.onFieldDragOver}
              onDrop={props.onFieldDrop}
              onFieldTitleChange={props.onFieldTitleChange}
              onFieldGroupingChange={props.onFieldGroupingChange}
              onFieldSortDirectionChange={props.onFieldSortDirectionChange}
              onFieldRemove={props.onFieldRemove}
              onToggleEquation={props.onToggleEquation}
              onOperatorChange={props.onOperatorChange}
              onNewValueChange={props.onNewValueChange}
              onFieldToUseChange={props.onFieldToUseChange}
            />
          )}
        </div>
      }
      <FieldsDialog
        formRenderProps={formRenderProps}
        onClose={props.onCloseFieldsDialog}
        onSaveFieldsDialog={props.onSaveFieldsDialog}
        onFieldCheck={props.onFieldCheck}
      />

      <style>
        {`
                        .datacorrection-page-form .k-form .table-section
                        {
                            width: 100%;
                            margin-top: ${
                              pageState.formValues.ViewMode == 1
                                ? "38px"
                                : "4px"
                            };
                            border: 1px solid ${
                              currentTheme.componentBorderColor
                            };
                            border-radius: 6px;
                            padding: 2px 4px 12px 4px;
                        }

                        .datacorrection-page-form .k-form .table-fields
                        {
                            margin-top:12px;
                        }

                        .datacorrection-page-form .k-form .table-fields
                        {
                            padding:4px;
                            /*border: 1px solid ${
                              currentTheme.componentBorderColor
                            };*/
                            width: 100%;
                        }

                        .datacorrection-page-form .k-form .table-fields .list-item
                        {
                            white-space:nowrap;
                            
                        }

                        .datacorrection-page-form .k-form .table-fields .list-item:hover
                        {
                            cursor: grab;
                            background-color: ${currentTheme.componentBgFaded};
                        }

                        .datacorrection-page-form .k-form .table-fields .list-item-title
                        {
                            vertical-align: middle;
                            font-size:0.85rem;
                            width: 280px;
                            overflow: hidden;
                            text-overflow: ellipsis;
                            white-space: nowrap;
                        }

                        .datacorrection-page-form .k-form .table-fields .list-item-delete
                        {
                            width: 40px;
                        }

                        .datacorrection-page-form .k-form .table-fields .list-item .cmb-function
                        {
                            
                        }

                        .datacorrection-page-form .k-form .table-fields .list-item .k-input
                        {                           
                            width:200px;
                        }

                        .datacorrection-page-form .table-datacorrection-actions-button:focus::after
                        {
                            border: none !important;
                            outline: none !important;
                            box-shadow: none !important;
                        }

                        .table-datacorrection-actions .k-menu-item:nth-child(2):hover,
                        .table-datacorrection-actions .k-menu-item:nth-child(4):hover,
                        .table-datacorrection-actions .k-menu-item:nth-child(6):hover,
                        .table-datacorrection-actions .k-menu-item:nth-child(8):hover
                        {
                            background-color: tranpsarent !important;
                            background: none !important;
                        }

                        .table-datacorrection-actions .k-menu-item:nth-child(2),
                        .table-datacorrection-actions .k-menu-item:nth-child(4),
                        .table-datacorrection-actions .k-menu-item:nth-child(6),
                        .table-datacorrection-actions .k-menu-item:nth-child(8)
                        {
                            padding: 0;
                        }
                        .table-datacorrection-actions .k-menu-item:nth-child(1),
                        .table-datacorrection-actions .k-menu-item:nth-child(3),
                        .table-datacorrection-actions .k-menu-item:nth-child(5),
                        .table-datacorrection-actions .k-menu-item:nth-child(7),
                        .table-datacorrection-actions .k-menu-item:nth-child(9)
                        {
                            padding-top: 7px;
                            padding-bottom: 7px;
                        }
                    `}
      </style>
    </Fragment>
  );
};

export const FieldsDialog = (props) => {
  const dispatch = useDispatch();
  const currentTheme = useSelector((state) => state.currentTheme);
  const appearance = useSelector((state) => state.appearance);
  const pageState = useSelector((state) => state.dataCorrectionPage);
  const { onSave, onClose, ...other } = props;
  const { t } = useTranslation();

  let fieldsToAdd = [...pageState.fieldsToAdd];

  return (
    <Fragment>
      {pageState.fieldsDialogOpen && (
        <Dialog className="datacorrection-fields-dialog" title={null}>
          <Form
            className="form"
            dispatch={dispatch}
            pageState={pageState}
            fields={pageState.fieldsToAdd}
            currentTheme={currentTheme}
            toolbarRenderer={(p) => toolbarRenderer({ ...p, t })}
            onSave={(e) => props.onSaveFieldsDialog(props.formRenderProps)}
            onClose={onClose}
            errorMessages={[]}
            onErrorbarClose={() => {}}
          >
            <List
              className="list"
              items={fieldsToAdd.filter((e) => e.IsVisible)}
              itemRenderer={dialogListItemRenderer(currentTheme)}
              onFieldCheck={props.onFieldCheck}
            />
          </Form>
        </Dialog>
      )}
      <style>
        {`
                    .datacorrection-fields-dialog
                    {
                        width:500px;
                        height: 500px;
                        left: calc(50% - 250px);
                        top: calc(50% - 250px);
                    }

                    .datacorrection-fields-dialog .k-dialog-content
                    {
                        padding: 0 !important;
                    }

                    .datacorrection-fields-dialog .k-dialog
                    {
                        min-width: 500px;
                        width: 500px;
                        height: 400px;
                        direction:${appearance.isRightToLeft ? "rtl" : "ltr"}
                    }

                    .datacorrection-fields-dialog .form
                    {
                        width: 100%;
                    }

                    .datacorrection-fields-dialog .list-container
                    {
                        border: none;
                        padding: 8px;
                        margin-top: 0px;
                    }

                    @media (max-width: 600px)
                    {
                        .datacorrection-fields-dialog
                        {
                            width: 100%;
                            height: 100%;
                            left: 0;
                            top: 0;
                        }

                        .datacorrection-fields-dialog .k-dialog
                        {
                            width: calc(100vw - 24px);
                            max-width: calc(100vw - 24px);
                            min-width: 0;
                            height: calc(100dvh - 24px);
                            max-height: calc(100dvh - 24px);
                        }

                        .datacorrection-fields-dialog .toolbar .k-toolbar
                        {
                            flex-wrap: wrap;
                            gap: 4px;
                            overflow: visible;
                        }

                        .datacorrection-fields-dialog .toolbar .k-separator
                        {
                            display: none;
                        }
                    }
                    `}
      </style>
    </Fragment>
  );
};
